import React, { useState, useEffect, useMemo } from "react";
import _ from "lodash";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../../common/components/loading/loading";
import { getTermHistory } from "../../../common/services/markets/termService";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

const TermHistory = ({ termId }) => {
  const { t } = useTranslation(["markets"]);
  const [termHistory, setTermHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch term history on component mount
  useEffect(() => {
    const fetchHistory = async () => {
      const data = await getTermHistory(termId);
      const formattedData = mapToViewModel(data);
      setTermHistory(_.orderBy(formattedData, "id", "desc"));
      setIsLoading(false);
    };

    fetchHistory();
  }, [termId]);

  // Map API data to view model with formatted dates
  const mapToViewModel = (terms) => {
    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };

    return terms.map((term) => ({
      id: term.id,
      name: term.name,
      termScheduleName: term.termScheduleName,
      active: term.active,
      updatedByUserAccountId: term.createdByUserAccountId,
      updatedBy:
        term.updatedByUserAccountFirstName +
        " " +
        term.updatedByUserAccountLastName,
      updatedDate: new Date(term.updatedDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      writeDate: new Date(term.writeDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      eventId: term.eventId,
    }));
  };

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "id",
        headerName: t("RecordID"),
        sortable: true,
        filter: true,
        sort: "desc",
      },
      {
        field: "name",
        headerName: t("TermName"),
        sortable: true,
        filter: true,
      },
      {
        field: "termScheduleName",
        headerName: t("TermSchedule"),
        sortable: true,
        filter: true,
      },
      {
        field: "active",
        headerName: t("Active"),
        flex: 1,
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        headerClass: "ag-center-aligned-header",
        filterValueGetter: (params) => params.data.activeString,
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
      {
        field: "updatedBy",
        headerName: t("UpdatedBy"),
        sortable: true,
        filter: true,
      },
      {
        field: "updatedDate",
        headerName: t("UpdateDate"),
        sortable: true,
        filter: true,
      },
      {
        field: "writeDate",
        headerName: t("WriteDate"),
        sortable: true,
        filter: true,
      },
      {
        field: "eventId",
        headerName: t("EventID"),
        sortable: true,
        filter: true,
      },
    ],
    [t]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 100,
      flex: 1,
    }),
    []
  );

  if (isLoading) return <Loading />;

  return (
    <div className="history-table-container">
      <div className="mb-3">
        {t("TermID")} {termId}
      </div>
      <ThemeableAgGrid
        className="table-extra-small"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={termHistory}
        getRowId={(params) => params.data.id.toString()}
        domLayout="autoHeight"
        animateRows={true}
      />
    </div>
  );
};

export default TermHistory;

